import { useState } from "react";
import { DataTable } from "@/components/admin/data-table";
import { DateField } from "@/components/admin/date-field";
import { ExportButton } from "@/components/admin/export-button";
import { FilterButton } from "@/components/admin/filter-form";
import { List } from "@/components/admin/list";
import { ListPagination } from "@/components/admin/list-pagination";
import { ReferenceField } from "@/components/admin/reference-field";
import { SearchInput } from "@/components/admin/search-input";
import { SelectInput } from "@/components/admin/select-input";
import { ToggleFilterButton } from "@/components/admin/toggle-filter-button";
import { CreateButton } from "@/components/admin/create-button";
import { Button } from "@/components/ui/button";
import { TopToolbar } from "../../layout/TopToolbar";
import { PaymentStatusBadge } from "./PaymentStatusBadge";
import { PaymentCalendar } from "./PaymentCalendar";
import {
  paymentDirectionChoices,
  paymentStatusChoices,
} from "./paymentModel";
import { TransactionDirectionBadge } from "../shared/TransactionDirectionBadge";
import { LayoutList, CalendarDays, XCircle } from "lucide-react";
import { useListContext, useTranslate } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { PlannedCashflowChart } from "../dashboard/PlannedCashflowChart";
import { PlannedBalanceWidget } from "../dashboard/PlannedBalanceWidget";
import type { Payment } from "../../types";

const paymentFilters = [
  <SearchInput source="q" alwaysOn />,
  <SelectInput
    source="direction"
    choices={paymentDirectionChoices}
    label="resources.payments.fields.direction"
  />,
  <SelectInput
    source="status"
    choices={paymentStatusChoices.filter((c) => c.id !== "paid")}
    label="resources.payments.fields.status"
  />,
];

export const PaymentList = () => {
  const [view, setView] = useState<"list" | "calendar">("list");

  return (
    <List
      perPage={view === "calendar" ? 1000 : 25}
      sort={{ field: "planned_date", order: "ASC" }}
      filter={{ "status@neq": "paid" }}
      filters={paymentFilters}
      actions={<PaymentListActions view={view} setView={setView} />}
      pagination={view === "list" ? <ListPagination rowsPerPageOptions={[10, 25, 50, 100]} /> : false}
    >
      {view === "list" && <PlannedBalanceWidget />}
      {view === "list" && <PlanningChartBlock />}
      <div className="mb-3 flex flex-wrap gap-2">
        <ToggleFilterButton
          label="resources.transactions.directions.income"
          value={{ direction: "income" }}
          className="w-auto"
        />
        <ToggleFilterButton
          label="resources.transactions.directions.expense"
          value={{ direction: "expense" }}
          className="w-auto"
        />
        <ToggleFilterButton
          label="resources.payments.filters.due_soon"
          value={{ status: "pending", "planned_date@lte": getDateOffset(2) }}
          className="w-auto"
        />
        <ToggleFilterButton
          label="resources.payments.filters.overdue"
          value={{ status: "overdue" }}
          className="w-auto"
        />
        <ClearFiltersButton />
      </div>

      {view === "list" ? (
        <DataTable<Payment>
          rowClick="show"
          rowClassName={(record) => {
            if (record.status === "overdue") {
              return "bg-rose-100/70 hover:bg-rose-100 dark:bg-rose-950/40 dark:hover:bg-rose-950/60";
            }
            if (record.status === "pending") {
              return "bg-amber-100/60 hover:bg-amber-100 dark:bg-amber-950/30 dark:hover:bg-amber-950/50";
            }
            return "";
          }}
        >
          <DataTable.Col
            source="direction"
            render={(r) => <TransactionDirectionBadge direction={r.direction} />}
          />
          <DataTable.Col
            source="vendor_name"
            label="resources.payments.fields.recipient"
            render={(r) =>
              r.vendor_name ? (
                <span>{r.vendor_name}</span>
              ) : r.contract_id ? (
                <ReferenceField
                  record={r as { id: number; contract_id: number | null }}
                  source="contract_id"
                  reference="contracts"
                  link="show"
                />
              ) : null
            }
          />
          <DataTable.Col source="category_id">
            <ReferenceField source="category_id" reference="transaction_categories" link={false} />
          </DataTable.Col>
          <DataTable.Col source="planned_date" field={DateField} />
          <DataTable.Col source="account_id" label="resources.payments.fields.account_id">
            <ReferenceField source="account_id" reference="accounts" link={false} />
          </DataTable.Col>
          <DataTable.Col
            source="amount"
            render={(r) => (
              <span
                className={
                  r.direction === "expense"
                    ? "text-rose-600 font-medium"
                    : "text-emerald-600 font-medium"
                }
              >
                {(r.direction === "expense" ? "−" : "+") +
                  Number(r.amount).toLocaleString("ru-RU") +
                  " ₽"}
              </span>
            )}
          />
          <DataTable.Col
            source="status"
            render={(record) => <PaymentStatusBadge status={record.status} />}
          />
          <DataTable.Col source="comment" />
        </DataTable>
      ) : (
        <PaymentCalendar />
      )}
    </List>
  );
};

const PaymentListActions = ({
  view,
  setView,
}: {
  view: "list" | "calendar";
  setView: (v: "list" | "calendar") => void;
}) => (
  <TopToolbar>
    <FilterButton />
    <div className="flex border rounded-md overflow-hidden">
      <Button
        variant={view === "list" ? "default" : "ghost"}
        size="sm"
        className="rounded-none"
        onClick={() => setView("list")}
      >
        <LayoutList className="h-4 w-4 mr-1" />
        Список
      </Button>
      <Button
        variant={view === "calendar" ? "default" : "ghost"}
        size="sm"
        className="rounded-none"
        onClick={() => setView("calendar")}
      >
        <CalendarDays className="h-4 w-4 mr-1" />
        Календарь
      </Button>
    </div>
    <ExportButton />
    <CreateButton label="resources.payments.action.new" />
  </TopToolbar>
);

const PlanningChartBlock = () => {
  const translate = useTranslate();
  return (
    <Card className="mb-3">
      <CardContent className="px-4 py-3 space-y-3">
        <h2 className="text-sm font-semibold">
          {translate("finance.forecast.chartTitle", { _: "Планируемые доходы/расходы по месяцам" })}
        </h2>
        <PlannedCashflowChart />
      </CardContent>
    </Card>
  );
};

const ClearFiltersButton = () => {
  const { filterValues, setFilters } = useListContext();
  if (!filterValues || Object.keys(filterValues).length === 0) return null;
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => setFilters({}, undefined, false)}
      className="text-muted-foreground"
    >
      <XCircle className="h-3 w-3 mr-1" />
      Сбросить фильтры
    </Button>
  );
};

const getDateOffset = (days: number) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
};
